import { BsWrenchAdjustable } from "react-icons/bs";
import { accessManagementIcon, dashboardIcon, serviceIcon, workOrderIcon } from "./svgs";

/**
 * Check if user has a specific permission for a menu
 * @param {string} menuSlug - The menu/section slug
 * @param {string} permissionSlug - The permission slug to check
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (menuSlug, permissionSlug) => {
  const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));

  // Super admin always has access
  if (userDetails?.my_role?.slug === "super-admin") {
    return true;
  }

  // If no user details or sidebar, deny access
  if (!userDetails || !userDetails.user_sidebar) {
    return false;
  }

  // Find the specific menu in user's sidebar
  const menuItem = userDetails.user_sidebar.find(item =>
    item.slug === menuSlug ||
    item.slug === `/${menuSlug}` ||
    `/${item.slug}` === `/${menuSlug}`
  );

  // Check if user has the required permission for this menu
  return menuItem &&
    menuItem.permissions &&
    menuItem.permissions.some(perm => perm.slug === permissionSlug || perm.name.toLowerCase() === permissionSlug.toLowerCase());
};

/**
 * Format date to Month DD, YYYY format
 * @param {string} dateString - The date string to format
 * @returns {string} - Formatted date string
 */
export const dateMonthYear = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) return '';

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  return date.toLocaleDateString('en-US', options);
};

export const sideMenuList = [
  {
    id: 1,
    heading: "Dashboard",
    url: "/dashboard",
    icon: dashboardIcon,
  },
  {
    id: 2,
    heading: "Issues",
    url: "/issue",
    icon: <BsWrenchAdjustable fontSize={"24px"} />,
  },
  {
    id: 3,
    heading: "Services",
    url: "/services",
    icon: serviceIcon,
  },
  {
    id: 4,
    heading: "Work Orders",
    url: "/work-orders",
    icon: workOrderIcon,
  },
  {
    id: 5,
    heading: "Access Management",
    url: "/access-management",
    icon: accessManagementIcon,
  }
  // {
  //   id: 2,
  //   heading: "Maintenance",
  //   url: "/maintenance",
  //   icon: <GrVmMaintenance fontSize={'22px'} />,
  // },
  // {
  //   id: 3,
  //   heading: "Tyre",
  //   url: "/tyre",
  //   icon: <GiTyre fontSize={'22px'} />,
  // }
];

export const customStyles = {
  tableWrapper: { style: { overflowX: "auto", overflow: "none" } },
  rows: {
    style: {
      fontSize: "14px",
      overflow: "none"
    },
  },
  headCells: {
    style: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#9C9FC9",
      paddingLeft: "25px",
      overflow: "none"
    },
  },
  cells: {
    style: {
      fontSize: "14px",
      color: "#3D3F5C",
      fontWeight: "400",
      paddingLeft: "25px",
      overflow: "none"
    },
  },
};

export const slugfy = (value) => {
  return value ? value.toLowerCase().replace(/ /g, '-') : '';
}

export const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['bold', 'italic', 'underline'],
    [{ 'align': [] }],
    ['link'],
  ],
  mention: {
    // Example static list, replace with your dynamic user list
    source: function (searchTerm, renderList) {
      const values = [
        { id: 1, value: 'admin' },
        { id: 2, value: 'Test Salman' },
        { id: 3, value: 'Sajid Khan' }
      ];
      if (searchTerm.length === 0) {
        renderList(values, searchTerm);
      } else {
        const matches = values.filter(entry =>
          entry.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
        renderList(matches, searchTerm);
      }
    },
    renderItem: function (item) {
      return `${item.value}`;
    },
    onSelect: function (item, insertItem) {
      insertItem(item);
    },
    mentionDenotationChars: ["@"],
    showDenotationChar: true,
  }
};

export const getCurrentUser = () => {
  const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));
  return userDetails || null;
}

export const formatLogAfter = (after, currentUser) => {
  if (!after) return '';
  return after.replace(/\[\[(.*?)\]\]/g, (match, username) => {
    if (username === currentUser?.username) return 'you';
    return username;
  });
};

export function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const options = { month: 'short', day: 'numeric' };
  const datePart = date.toLocaleDateString(undefined, options);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${datePart} at ${hours}:${minutes} ${ampm}`;
}

// define some light background colors & matching text colors
const bgColors = [
  "#E3F2FD", "#FFF3E0", "#E8F5E9", "#F3E5F5", "#E0F7FA", "#FFFDE7", "#FBE9E7"
];
const textColors = [
  "#1565C0", "#EF6C00", "#2E7D32", "#6A1B9A", "#00838F", "#F9A825", "#D84315"
];

// helper function to pick color based on username (stable random)
export const getColorSet = (str) => {
  if (!str) return { bg: "#e0e0e0", color: "#333" };
  const index = str.charCodeAt(0) % bgColors.length;
  return { bg: bgColors[index], color: textColors[index] };
};

export const timeAgo = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 1) return 'just now';
  if (seconds < 60) return `${seconds} sec ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
};